import { useEffect, useMemo, useState } from 'react';
import { Box, Center, Flex } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import { useSelector } from 'react-redux';

import { ServiceConstants } from 'constant';
import { LinkResponse } from 'types';

import { IRootState } from 'config/redux';
import Modal from './base';
import Button from '../button/standard';
import Text from '../text';
import { TFunction } from 'i18next';
import { Link, Title } from 'components';
import { AppUtils, UserUtils } from 'utils';

const { REQUEST_PENDING } = ServiceConstants;

interface PublicLinkModalProps {
  opened: boolean;
  onClose: (val: boolean) => void;
  create: () => void;
  requestId: string;
  t: TFunction;
  link?: LinkResponse;
  authority: string;
  object: 'credit' | 'card';
}

const PublicLinkModal = ({ opened, onClose, create, requestId, t, link, authority, object }: PublicLinkModalProps) => {

  const serviceObject = useSelector((state: IRootState) => state.service.object[requestId])
  const [tempLink, setTempLink] = useState<LinkResponse | undefined>(link);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, requestId, setTempLink);
  }, [serviceObject])

  const isLoading = useMemo(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);
  const fullUrl = useMemo(() => {
    if (!tempLink) {
      return undefined;
    }
    const translated = object === 'credit' ? 'kredit' : 'karte';
    return `${process.env.REACT_APP_PUBLIC_LINK}/${translated}/${tempLink.id}`
  }, [tempLink, object])

  return (
    <Modal opened={opened} onClose={() => onClose(false)} title={t('link.title')}>
      <Text>{t('link.description')}</Text>
      <Box mt={'sm'}>
        {tempLink &&
          <>
            <Title order={4} mb={'sm'} ta={'center'}>
              {fullUrl}
              <Link ml={'sm'} onClick={() => navigator.clipboard.writeText(fullUrl!)}><IconCopy /></Link>
            </Title>
            <Text ta={'center'}>{t('link.code')}</Text>
            <Center>
              <Flex>
                {tempLink.accessCode.split('').map((el, index) =>
                  <Title order={3} mb={'sm'} ta={'center'} key={`code-${index}`} ml={index !== 0 ? 'xs' : ''}>{el}</Title>
                )}
              </Flex>
            </Center>
          </>
        }
      </Box>
      {UserUtils.hasAuthority(authority) &&
        <>
          <Box mt={20}>
            <Button
              fullWidth={true}
              disabled={isLoading}
              loading={isLoading}
              onClick={create}
            >
              {t('action.create')}
            </Button>
            {tempLink && <Text size={'xs'} ta={'center'} c={'var(--mantine-color-gray-6)'} mt={'xs'}>{t('link.recreate')}</Text>}
          </Box>
        </>
      }
    </Modal>
  )
}

export default PublicLinkModal;